import { Seo, JsonLd, breadcrumbLd } from "@/components/site/Seo";
import { SITE } from "@/config/site";
import { MEDIA } from "@/data/media";
import { Breadcrumbs, Container, Eyebrow, PhotoFrame, Reveal, SectionHeading, TextLink } from "@/components/site/Primitives";
import { CatalogueRequest, FinalCta } from "@/components/site/Blocks";

const FOCUS = [
  { t: "Printing", d: "Custom printing on clothing, gifts and promotional items." },
  { t: "Branding", d: "Helping businesses and organisations present their brand consistently." },
  { t: "Custom apparel", d: "T-shirts, golf shirts, jackets and uniforms made personal." },
  { t: "Personalised gifts", d: "Meaningful gifts with names, photos and messages." },
  { t: "Xhosa cultural products", d: "Products that celebrate heritage, clan names and identity." },
  { t: "Customer service", d: "Direct, personal communication from enquiry to collection." },
  { t: "Community development", d: "A long-term ambition to create jobs and skills for women and young people." },
];

export default function About() {
  return (
    <>
      <Seo title="About KhozaLand | Women-Led Printing & Branding Business in Cape Town" description="KhozaLand (Pty) Ltd is a 100% women-led printing and branding business founded in 2020 by Yolanda Mketo in Mitchells Plain, Cape Town. Power of Dreams." path="/about" />
      <section className="bg-white">
        <Container className="pt-6"><Breadcrumbs items={[{ name: "About KhozaLand", path: "/about" }]} /></Container>
        <Container className="grid gap-12 pb-16 pt-8 lg:grid-cols-12 lg:items-center lg:pb-24 lg:pt-12">
          <div className="lg:col-span-6">
            <Eyebrow>Our story</Eyebrow>
            <h1 className="kl-h1 mt-5">About KhozaLand</h1>
            <p className="mt-6 text-base leading-7 text-black/70 sm:text-lg sm:leading-8">
              KhozaLand (Pty) Ltd is a South African, 100% women-led printing and branding business, founded in 2020 by <strong className="font-semibold text-[#161616]">{SITE.founder}</strong> and based in Mitchells Plain, Cape Town.
            </p>
            <p className="mt-4 text-base leading-7 text-black/70">
              We create personalised products and custom branding for individuals, families, businesses and organisations — from branded clothing and corporate gifts to personalised keepsakes and Xhosa cultural products that celebrate identity and heritage.
            </p>
            <p className="mt-8 font-display text-3xl tracking-tight sm:text-4xl">{SITE.motto}</p>
          </div>
          <div className="grid grid-cols-2 items-start gap-4 lg:col-span-6">
            <PhotoFrame media={MEDIA.blanketMngwevu} ratio="4/5" eager sizes="(min-width:1024px) 24vw, 46vw" />
            <PhotoFrame media={MEDIA.candlesChurch} ratio="4/5" className="mt-10" sizes="(min-width:1024px) 24vw, 46vw" />
          </div>
        </Container>
      </section>

      <section className="bg-[#161616] py-16 text-white lg:py-24" aria-labelledby="journey-title">
        <Container className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading light eyebrow="Since 2020" title="Our journey" id="journey-title" />
          </div>
          <div className="space-y-5 text-base leading-7 text-white/75 lg:col-span-7 sm:text-lg">
            <p>Since 2020, KhozaLand has grown from personalised gifts into a broader printing and branding offering — custom T-shirts, golf shirts, jackets, uniforms, mugs, tumblers, cushions, candles and cultural products.</p>
            <p>Our customers include families celebrating milestones, churches and burial societies dressing their members, community organisations commemorating events, and businesses building their brands.</p>
            <p>Alongside commercial branding, KhozaLand creates culturally meaningful products such as personalised Ingcawe/Ngcawe blankets printed with clan names and iziduko — helping customers express who they are.</p>
          </div>
        </Container>
      </section>

      <section className="py-16 lg:py-24" aria-labelledby="mv-title">
        <Container>
          <h2 id="mv-title" className="sr-only">Mission and vision</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Reveal className="kl-card p-8 sm:p-10">
              <p className="kl-eyebrow text-black/50">Mission</p>
              <p className="mt-4 font-display text-2xl leading-snug tracking-tight sm:text-[28px]">Provide quality personalised printing and branding products that help customers express their identity and bring their ideas to life.</p>
            </Reveal>
            <Reveal delay={80} className="kl-card bg-[#F7F7F7] p-8 sm:p-10">
              <p className="kl-eyebrow text-black/50">Vision</p>
              <p className="mt-4 font-display text-2xl leading-snug tracking-tight sm:text-[28px]">Grow into a recognised printing, branding and custom apparel business while creating employment and skills-development opportunities.</p>
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="focus-title">
        <Container>
          <SectionHeading eyebrow="What we focus on" title="Built around people, identity and good service" id="focus-title" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FOCUS.map((f, i) => (
              <Reveal key={f.t} delay={(i % 4) * 60} className="kl-card p-6">
                <h3 className="text-lg font-semibold">{f.t}</h3>
                <p className="mt-2 text-sm leading-6 text-black/65">{f.d}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 lg:py-24" aria-labelledby="women-title">
        <Container className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Women-led" title="Women's entrepreneurship, rooted in community" id="women-title" lead="KhozaLand is 100% women-owned and led. Our long-term dream is to grow local manufacturing capability and create employment and practical skills for women and young people in our community." />
            <div className="mt-7 flex flex-wrap gap-5">
              <TextLink to="/gallery" testId="about-gallery-link">View our work</TextLink>
              <TextLink to="/contact" testId="about-contact-link">Request a quote</TextLink>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-[18px] border border-black/10 bg-black/10 lg:col-span-5">
            {[["Founded", String(SITE.foundingYear)], ["Ownership", "100% women-led"], ["Based in", "Mitchells Plain"], ["Serving", "Cape Town & beyond"]].map(([k, val]) => (
              <div key={k} className="bg-white p-6"><dt className="kl-eyebrow text-black/50">{k}</dt><dd className="mt-2 text-lg font-semibold">{val}</dd></div>
            ))}
          </dl>
        </Container>
      </section>

      <CatalogueRequest />
      <FinalCta />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "AboutPage", name: "About KhozaLand", about: { "@type": "Organization", name: SITE.legalName, founder: { "@type": "Person", name: SITE.founder }, foundingDate: String(SITE.foundingYear) } }} />
    </>
  );
}

export { breadcrumbLd };
